/**
 * Sign-in for everyone: an email, then the six-digit code sent to it. Customers, clinic members and
 * staff all sign in here. Afterwards the browser goes on to finish a second factor when the account
 * needs one, then to `next`, or to the account's home area: the admin app, the clinic portal or
 * the customer account.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useRef, useState } from "react";
import { homePathFor, secondFactorPathFor } from "@lattice/core";
import { Button } from "@lattice/ui/Button";
import { loadSession, requestSignIn, signInWithCode } from "./api.ts";
import { authStyles, setCodeAsked } from "./AuthLayout.tsx";
import { EmailLinkForm } from "./EmailLinkForm.tsx";
import { turnstileToken } from "./turnstile.ts";
const PROBLEMS = new Map([["wrong", "That code didn't match, or it has expired. Check the newest email."], ["too_many", "Too many tries for this code. Ask for a new one."], ["failed", "We couldn't sign you in. Try again in a moment."]]);

/** How long "Send a new code" rests after a send, so two presses are not two emails by accident. */
const RESEND_REST_SECONDS = 30;
const RESEND_PROBLEMS = new Map([["too_many", "You've asked five times in the last hour. Use the newest email, or try again later."], ["failed", "We couldn't send a new code. Try again in a moment."]]);

/** Where to go once signed in: a second factor first if the account needs one. */
async function onward(next: string | null): Promise<void> {
  const loaded = await loadSession();
  if (loaded.status !== "ok") {
    window.location.assign(next ?? "/account");
    return;
  }
  const destination = next ?? homePathFor(loaded.me);
  const finish = secondFactorPathFor(loaded.me);
  window.location.assign(finish === null ? destination : `${finish}?next=${encodeURIComponent(destination)}`);
}
function CodeStep({
  email,
  next,
  submitLabel,
  onBack
}: {
  readonly email: string;
  readonly next: string | null;
  readonly submitLabel: string;
  readonly onBack: () => void;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resentAt, setResentAt] = useState<string | null>(null);
  const [rest, setRest] = useState(0);
  /* Five codes an hour is the limit, so once it is reached the button stays off and says why. */
  const [limited, setLimited] = useState(false);
  const field = useRef<HTMLInputElement>(null);

  /* The email form this step replaces held focus, so without this it falls to the page body. */
  useEffect(() => {
    field.current?.focus();
  }, []);
  useEffect(() => {
    if (rest <= 0) return;
    const timer = window.setTimeout(() => setRest(left => left - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [rest]);
  const submit = async () => {
    setBusy(true);
    setProblem(null);
    const outcome = await signInWithCode(email, code.replace(/[\s-]/g, "")).catch(() => "failed" as const);
    if (outcome === "ok") {
      await onward(next);
      return;
    }
    setBusy(false);
    setProblem(PROBLEMS.get(outcome) ?? null);
  };
  const resend = async () => {
    setResending(true);
    setProblem(null);
    const outcome = await turnstileToken().then(token => requestSignIn(email, token)).catch(() => "failed" as const);
    setResending(false);
    if (outcome === "sent") {
      setResentAt(new Date().toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit"
      }));
      setCode("");
      setRest(RESEND_REST_SECONDS);
      field.current?.focus();
      return;
    }
    if (outcome === "too_many") {
      /* The paragraph under the button says it, so the field does not say it a second time. */
      setLimited(true);
      return;
    }
    setProblem(RESEND_PROBLEMS.get(outcome) ?? RESEND_PROBLEMS.get("failed") ?? null);
  };
  return <form {...stylex.props(authStyles.form)} onSubmit={event => {
    event.preventDefault();
    if (!busy) void submit();
  }}>
      <p {...stylex.props(authStyles.body)} role="status" data-testid="code-sent">
        {resentAt === null ? "We sent a code to " : "We sent a new code to "}
        {email}
        {resentAt === null ? "" : ` at ${resentAt}`}. It works for 10 minutes.
      </p>
      <div {...stylex.props(authStyles.field)}>
        <label htmlFor="sign-in-code" {...stylex.props(authStyles.label)}>
          Six-digit code
        </label>
        <input ref={field} id="sign-in-code" data-testid="sign-in-code" inputMode="numeric" autoComplete="one-time-code" aria-invalid={problem === null ? undefined : true} aria-describedby={problem === null ? undefined : "sign-in-code-problem"} value={code} onChange={event => setCode(event.target.value)} {...stylex.props(authStyles.input, authStyles.code)} />
        {problem === null ? null : <p id="sign-in-code-problem" role="alert" {...stylex.props(authStyles.error)}>
            {problem}
          </p>}
      </div>
      <Button type="submit" busy={busy} testId="sign-in-submit" wide>
        {submitLabel}
      </Button>
      <Button tone="quiet" size="md" busy={resending} disabled={rest > 0 || limited} describedBy={limited ? "resend-limit" : undefined} testId="resend-code" onClick={() => void resend()}>
        {rest > 0 ? `Send a new code in ${String(rest).padStart(2, "0")}s` : "Send a new code"}
      </Button>
      {limited ? <p id="resend-limit" role="alert" {...stylex.props(authStyles.body)} data-testid="resend-limit">
          You've asked for five codes in the last hour. Use the newest email, or ask again in an
          hour.
        </p> : null}
      <Button tone="quiet" size="md" onClick={onBack}>
        Use a different email
      </Button>
    </form>;
}

/**
 * `submitLabel` names what the code does where the person already has a session, such as
 * confirming their email at checkout. The code signs in either way.
 */
export function EmailCodeSignIn({
  next,
  submitLabel = "Sign in"
}: {
  readonly next: string | null;
  readonly submitLabel?: string;
}) {
  const [email, setEmail] = useState<string | null>(null);
  useEffect(() => {
    setCodeAsked(email !== null);
    return () => setCodeAsked(false);
  }, [email]);
  if (email === null) return <EmailLinkForm submitLabel="Email me a code" onSent={setEmail} request={requestSignIn} />;
  return <CodeStep email={email} next={next} submitLabel={submitLabel} onBack={() => setEmail(null)} />;
}